/**
 * ProgramExecutor Service
 *
 * Executes visual programs step-by-step with animations. Parses code block
 * sequences, executes commands, handles loops, and tracks execution state
 * for the Programming discipline challenges.
 */

import type { CodeBlockData, BlockType, Direction, Position, Collectible } from "./types";

/**
 * Current state of program execution
 */
export interface ProgramState {
  /** Current position [row, col] */
  position: Position;
  /** Direction the runner is facing */
  direction: Direction;
  /** IDs of collected items */
  collected: string[];
  /** Total steps taken */
  steps: number;
  /** Whether program has completed (reached end or failed) */
  completed: boolean;
  /** Whether the goal was reached successfully */
  success: boolean;
  /** Whether an obstacle was hit */
  hitObstacle: boolean;
  /** Index of the currently executing block */
  currentBlockIndex: number;
  /** ID of the currently executing block */
  currentBlockId: string | null;
}

/**
 * Result after program execution completes
 */
export interface ExecutionResult {
  /** Whether the goal was reached */
  success: boolean;
  /** Total steps taken */
  steps: number;
  /** Optimal number of steps for this maze */
  optimalSteps: number;
  /** Items collected during execution */
  collected: string[];
  /** Total collectibles available */
  totalCollectibles: number;
  /** Whether all collectibles were gathered */
  allCollected: boolean;
  /** Whether execution hit an obstacle */
  hitObstacle: boolean;
  /** Final position */
  finalPosition: Position;
}

/**
 * Execution speed modes
 */
export type ExecutionSpeed = "normal" | "fast" | "step";

/**
 * Speed settings in milliseconds
 */
export const EXECUTION_SPEEDS: Record<ExecutionSpeed, number> = {
  normal: 500, // 500ms per step
  fast: 150, // 150ms per step (quick preview)
  step: 0, // Manual advance (no auto-delay)
};

/**
 * Maze configuration for execution
 */
export interface MazeConfig {
  /** Grid dimensions [rows, cols] */
  size: [number, number];
  /** Starting position */
  start: Position;
  /** Goal position */
  end: Position;
  /** Obstacle positions */
  obstacles: Position[];
  /** Collectibles with positions and types */
  collectibles: Array<{ position: Position; type: string; id: string }>;
  /** Optimal number of moves to reach goal */
  optimalMoves: number;
}

/**
 * Convert block type to direction command
 */
function blockToDirection(blockType: BlockType): Direction | null {
  switch (blockType) {
    case "move_up":
      return "up";
    case "move_down":
      return "down";
    case "move_left":
      return "left";
    case "move_right":
      return "right";
    default:
      return null;
  }
}

/**
 * Flatten code blocks, expanding repeat loops
 */
export function flattenBlocks(blocks: CodeBlockData[]): CodeBlockData[] {
  const result: CodeBlockData[] = [];

  for (const block of blocks) {
    if (block.type === "repeat" && block.children && block.repeatCount) {
      // Expand loop: repeat the children N times
      for (let i = 0; i < block.repeatCount; i++) {
        result.push(...flattenBlocks(block.children));
      }
    } else {
      result.push(block);
    }
  }

  return result;
}

/**
 * Check if two positions are equal
 */
function positionsEqual(a: Position, b: Position): boolean {
  return a[0] === b[0] && a[1] === b[1];
}

/**
 * Check if position is within grid bounds
 */
function isInBounds(pos: Position, size: [number, number]): boolean {
  const [row, col] = pos;
  const [rows, cols] = size;
  return row >= 0 && row < rows && col >= 0 && col < cols;
}

/**
 * Check if position hits an obstacle
 */
function hitsObstacle(pos: Position, obstacles: Position[]): boolean {
  return obstacles.some((obs) => positionsEqual(obs, pos));
}

/**
 * Get the next position after moving in a direction
 */
function getNextPosition(current: Position, direction: Direction): Position {
  const [row, col] = current;

  switch (direction) {
    case "up":
      return [row - 1, col];
    case "down":
      return [row + 1, col];
    case "left":
      return [row, col - 1];
    case "right":
      return [row, col + 1];
  }
}

/**
 * Create initial program state
 */
export function createInitialState(maze: MazeConfig): ProgramState {
  return {
    position: [...maze.start],
    direction: "right", // Default facing right
    collected: [],
    steps: 0,
    completed: false,
    success: false,
    hitObstacle: false,
    currentBlockIndex: -1,
    currentBlockId: null,
  };
}

/**
 * Execute a single step of the program
 * Returns the new state after executing one command
 */
export function executeStep(
  state: ProgramState,
  block: CodeBlockData,
  blockIndex: number,
  maze: MazeConfig
): ProgramState {
  const direction = blockToDirection(block.type);

  // If not a movement block, skip (shouldn't happen with flattened blocks)
  if (!direction) {
    return {
      ...state,
      currentBlockIndex: blockIndex,
      currentBlockId: block.id,
    };
  }

  const nextPos = getNextPosition(state.position, direction);

  // Check bounds
  if (!isInBounds(nextPos, maze.size)) {
    // Can't move outside grid - stay in place but count as step
    return {
      ...state,
      direction,
      steps: state.steps + 1,
      currentBlockIndex: blockIndex,
      currentBlockId: block.id,
    };
  }

  // Check obstacles
  if (hitsObstacle(nextPos, maze.obstacles)) {
    return {
      ...state,
      direction,
      steps: state.steps + 1,
      completed: true,
      hitObstacle: true,
      currentBlockIndex: blockIndex,
      currentBlockId: block.id,
    };
  }

  // Valid move - update position
  const newState: ProgramState = {
    ...state,
    position: nextPos,
    direction,
    steps: state.steps + 1,
    currentBlockIndex: blockIndex,
    currentBlockId: block.id,
  };

  // Check for collectible at new position
  const collectibleAtPos = maze.collectibles.find(
    (c) => positionsEqual(c.position, nextPos) && !state.collected.includes(c.id)
  );
  if (collectibleAtPos) {
    newState.collected = [...state.collected, collectibleAtPos.id];
  }

  // Check if reached goal
  if (positionsEqual(nextPos, maze.end)) {
    newState.completed = true;
    newState.success = true;
  }

  return newState;
}

/**
 * Execute the entire program synchronously (for validation)
 * Returns the final result without animations
 */
export function executeProgram(
  blocks: CodeBlockData[],
  maze: MazeConfig
): ExecutionResult {
  const flatBlocks = flattenBlocks(blocks);
  let state = createInitialState(maze);

  // Execute each block
  for (let i = 0; i < flatBlocks.length; i++) {
    const block = flatBlocks[i];
    if (!block) continue;

    state = executeStep(state, block, i, maze);

    // Stop if hit obstacle or completed
    if (state.completed) {
      break;
    }
  }

  // Mark as completed even if didn't reach goal
  state.completed = true;

  return {
    success: state.success,
    steps: state.steps,
    optimalSteps: maze.optimalMoves,
    collected: state.collected,
    totalCollectibles: maze.collectibles.length,
    allCollected: state.collected.length === maze.collectibles.length,
    hitObstacle: state.hitObstacle,
    finalPosition: state.position,
  };
}

/**
 * Calculate stars earned based on execution result
 *
 * Star calculation for Programming:
 * - 3 stars: Optimal solution + all collectibles
 * - 2 stars: Completed + most collectibles (50%+)
 * - 1 star: Just completed
 * - 0 stars: Did not complete (hit obstacle or didn't reach goal)
 */
export function calculateStars(result: ExecutionResult): 0 | 1 | 2 | 3 {
  // Must complete to earn any stars
  if (!result.success) {
    return 0;
  }

  // 3 stars: Optimal solution + all collectibles
  if (result.steps <= result.optimalSteps && result.allCollected) {
    return 3;
  }

  // 2 stars: Completed + collected at least half
  const halfCollectibles = Math.floor(result.totalCollectibles / 2);
  if (result.collected.length >= halfCollectibles || result.totalCollectibles === 0) {
    // If no collectibles, check if solution is reasonably efficient
    if (result.totalCollectibles === 0 && result.steps <= result.optimalSteps * 1.5) {
      return 3; // Efficient solution with no collectibles = 3 stars
    }
    return 2;
  }

  // 1 star: Just completed
  return 1;
}

/**
 * Async program executor for animated step-by-step execution
 *
 * @param blocks - The program to execute
 * @param maze - Maze configuration
 * @param onStep - Callback called after each step with current state
 * @param speed - Execution speed mode
 * @returns Promise that resolves with the execution result
 */
export async function executeProgramAsync(
  blocks: CodeBlockData[],
  maze: MazeConfig,
  onStep: (state: ProgramState, isCollecting: boolean) => void,
  speed: ExecutionSpeed = "normal"
): Promise<ExecutionResult> {
  const flatBlocks = flattenBlocks(blocks);
  let state = createInitialState(maze);
  const delay = EXECUTION_SPEEDS[speed];

  // Initial state callback
  onStep(state, false);

  // Small initial delay to show starting position
  if (delay > 0) {
    await new Promise((resolve) => setTimeout(resolve, delay / 2));
  }

  // Execute each block
  for (let i = 0; i < flatBlocks.length; i++) {
    const block = flatBlocks[i];
    if (!block) continue;

    const prevCollected = state.collected.length;
    state = executeStep(state, block, i, maze);
    const justCollected = state.collected.length > prevCollected;

    // Callback with current state
    onStep(state, justCollected);

    // Wait between steps (unless step mode)
    if (delay > 0) {
      // Extra delay for collection animation
      const stepDelay = justCollected ? delay * 1.5 : delay;
      await new Promise((resolve) => setTimeout(resolve, stepDelay));
    }

    // Stop if completed (hit obstacle or reached goal)
    if (state.completed) {
      break;
    }
  }

  // Mark as completed if not already
  if (!state.completed) {
    state = { ...state, completed: true };
    onStep(state, false);
  }

  return {
    success: state.success,
    steps: state.steps,
    optimalSteps: maze.optimalMoves,
    collected: state.collected,
    totalCollectibles: maze.collectibles.length,
    allCollected: state.collected.length === maze.collectibles.length,
    hitObstacle: state.hitObstacle,
    finalPosition: state.position,
  };
}

/**
 * Validate if a program would succeed (without animation)
 * Useful for checking optimal solutions or hints
 */
export function validateProgram(
  blocks: CodeBlockData[],
  maze: MazeConfig
): { isValid: boolean; result: ExecutionResult } {
  const result = executeProgram(blocks, maze);
  return {
    isValid: result.success,
    result,
  };
}

/**
 * Get a preview of the path the program would take
 * Returns array of positions (useful for path visualization)
 */
export function getPathPreview(
  blocks: CodeBlockData[],
  maze: MazeConfig
): Position[] {
  const flatBlocks = flattenBlocks(blocks);
  const path: Position[] = [maze.start];
  let currentPos: Position = [...maze.start];

  for (const block of flatBlocks) {
    const direction = blockToDirection(block.type);
    if (!direction) continue;

    const nextPos = getNextPosition(currentPos, direction);

    // Stop at obstacles
    if (hitsObstacle(nextPos, maze.obstacles)) {
      break;
    }

    // Stay in bounds
    if (!isInBounds(nextPos, maze.size)) {
      continue;
    }

    path.push(nextPos);
    currentPos = nextPos;

    // Stop at goal
    if (positionsEqual(nextPos, maze.end)) {
      break;
    }
  }

  return path;
}
